import { useEffect, useState } from 'react';
import { useAuth } from './AuthProvider';
import { Box, Typography, CircularProgress, Alert } from '@mui/material';
import { useParams, useNavigate } from 'react-router';
import { JaaRoute } from '../../utilities/Links';

const VALID_SSO_CONNECTIONS = [
  'atad-juristiliitto-oidc',
  'atad-almamedia-waad',
] as const;

type ValidSsoConnection = (typeof VALID_SSO_CONNECTIONS)[number];

/**
 * A component that immediately triggers the SSO login flow
 * using a connection ID specified in the URL path (e.g., /sso-login/atad-juristiliitto-oidc).
 */
export default function SsoLoginRedirect() {
  const { actions, loading } = useAuth();
  const { connection } = useParams<{ connection: string }>();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Wait for auth to initialize
    if (loading) {
      return;
    }

    if (!connection) {
      setError('Virheellinen SSO-yhteys: yhteystietoa ei määritelty');
      const timer = setTimeout(() => navigate(JaaRoute.frontPage), 3000);
      return () => clearTimeout(timer);
    }

    // Validate that connection starts with 'atad-'
    if (!connection.startsWith('atad-')) {
      setError(`Virheellinen SSO-yhteys: ${connection}`);
      const timer = setTimeout(() => navigate(JaaRoute.frontPage), 3000);
      return () => clearTimeout(timer);
    }

    // Optional: Warn if connection is not in the known list
    if (!VALID_SSO_CONNECTIONS.includes(connection as ValidSsoConnection)) {
      console.warn(
        `Unknown SSO connection: ${connection}. Attempting login anyway.`,
      );
    }

    if (actions) {
      actions.login(connection, false);
    }
  }, [actions, connection, navigate, loading]);

  if (error) {
    return (
      <Box
        display='flex'
        flexDirection='column'
        justifyContent='center'
        alignItems='center'
        minHeight='30vh'
        gap={2}
        bgcolor='background.default'
        padding={2}
      >
        <Alert severity='error' sx={{ maxWidth: 600 }}>
          {error}
        </Alert>
        <Typography variant='body2' color='text.secondary'>
          Ohjataan takaisin etusivulle...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      display='flex'
      flexDirection='column'
      justifyContent='center'
      alignItems='center'
      minHeight='30vh'
      gap={2}
      bgcolor='background.default'
    >
      <CircularProgress />
      <Typography variant='h6'>Ohjataan kirjautumiseen...</Typography>
    </Box>
  );
}
